import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, ChevronDown, Loader2, MoreVertical, Plus, Search, X } from 'lucide-react';

type ApiBrandRow = {
  id: number;
  brand_name: string;
  calculation_id: number | null;
  calculation_description: string | null;
  metrics: unknown;
  favourite: boolean;
};

type CalcOption = 'exact_cost' | 'margin_percentage' | 'margin_by_qty';

function calcOptionFromCalculationId(calculationId: number | null): CalcOption {
  // Based on current backend meanings:
  // 1 = Exact Cost, 2 = Margin Percentage, 3 = Margin Percentage by Quantity
  if (calculationId === 2) return 'margin_percentage';
  if (calculationId === 3) return 'margin_by_qty';
  return 'exact_cost';
}

type ApiProductOption = {
  id: number;
  sku: string | null;
  main_title: string | null;
};

type MarginMatrixRow = {
  key: string;
  marginPercent: string; // input as %
  quantity: string; // int
  sellingPrice: string; // price
};

function makeRowKey() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function BrandManagementPage() {
  const [brands, setBrands] = useState<ApiBrandRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingBrandIds, setUpdatingBrandIds] = useState<Set<number>>(new Set());

  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [calcBrand, setCalcBrand] = useState<ApiBrandRow | null>(null);
  const [calcOption, setCalcOption] = useState<CalcOption>('exact_cost');
  const [marginPercent, setMarginPercent] = useState<string>('');
  const [calcSaving, setCalcSaving] = useState(false);
  const [calcSaveError, setCalcSaveError] = useState<string | null>(null);

  // Margin-by-quantity UI state
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productOptions, setProductOptions] = useState<ApiProductOption[]>([]);
  const [productFilter, setProductFilter] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [groupName, setGroupName] = useState<string>('');
  const [matrixRows, setMatrixRows] = useState<MarginMatrixRow[]>([
    { key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' },
  ]);

  // Filter brands based on search
  const filteredBrands = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => {
      if ((b.brand_name || '').toLowerCase().includes(q)) return true;
      if ((b.calculation_description || '').toLowerCase().includes(q)) return true;
      return false;
    });
  }, [brands, searchQuery]);

  async function setBrandFavourite(params: { brandId: number; favourite: boolean }) {
    const { brandId, favourite } = params;
    const prevFavourite = brands.find((b) => b.id === brandId)?.favourite;
    setUpdatingBrandIds((s) => new Set([...Array.from(s), brandId]));
    setBrands((current) => current.map((b) => (b.id === brandId ? { ...b, favourite } : b)));

    try {
      const resp = await fetch(`/v1/api/gp-reporter/brands/${brandId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ favourite }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
    } catch (e) {
      if (typeof prevFavourite === 'boolean') {
        setBrands((current) => current.map((b) => (b.id === brandId ? { ...b, favourite: prevFavourite } : b)));
      }
      setError(e instanceof Error ? e.message : 'Failed to update favourite.');
    } finally {
      setUpdatingBrandIds((s) => {
        const next = new Set(s);
        next.delete(brandId);
        return next;
      });
    }
  }

  async function loadBrands() {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/brands/', { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const data = (await resp.json()) as unknown;
      const arr = Array.isArray(data) ? data : [];
      const parsed: ApiBrandRow[] = arr
        .map((x) => {
          const id = Number((x as any)?.id);
          const brand_name = String((x as any)?.brand_name ?? '').trim();
          const calculation_id_raw = (x as any)?.calculation_id;
          const calculation_id =
            calculation_id_raw === null || calculation_id_raw === undefined ? null : Number(calculation_id_raw);
          const calculation_description_raw = (x as any)?.calculation_description;
          const calculation_description =
            calculation_description_raw === null || calculation_description_raw === undefined
              ? null
              : String(calculation_description_raw).trim();
          const metrics = (x as any)?.metrics;
          const favourite = Boolean((x as any)?.favourite);
          if (!Number.isFinite(id) || !brand_name) return null;
          return {
            id,
            brand_name,
            calculation_id: Number.isFinite(calculation_id as number) ? (calculation_id as number) : null,
            calculation_description: calculation_description || null,
            metrics,
            favourite,
          };
        })
        .filter(Boolean) as ApiBrandRow[];

      setBrands(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load brands.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadBrands();
  }, []);

  async function loadProductsForBrand(brandId: number) {
    setProductsLoading(true);
    setProductsError(null);
    try {
      const resp = await fetch(`/v1/api/gp-reporter/products/?brand_id=${encodeURIComponent(String(brandId))}&limit=500`, {
        method: 'GET',
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const data = (await resp.json()) as unknown;
      const arr = Array.isArray(data) ? data : [];
      const parsed: ApiProductOption[] = arr
        .map((x) => {
          const id = Number((x as any)?.id);
          const skuRaw = (x as any)?.sku;
          const mainTitleRaw = (x as any)?.main_title;
          if (!Number.isFinite(id)) return null;
          return {
            id,
            sku: skuRaw === null || skuRaw === undefined ? null : String(skuRaw),
            main_title: mainTitleRaw === null || mainTitleRaw === undefined ? null : String(mainTitleRaw),
          };
        })
        .filter(Boolean) as ApiProductOption[];
      setProductOptions(parsed);
    } catch (e) {
      setProductsError(e instanceof Error ? e.message : 'Failed to load products.');
      setProductOptions([]);
    } finally {
      setProductsLoading(false);
    }
  }

  function openCalcDialog(brand: ApiBrandRow) {
    setCalcBrand(brand);
    setCalcOption(calcOptionFromCalculationId(brand.calculation_id));

    // Pre-fill margin input if current brand uses calculation_id=2 with metrics.margin (decimal).
    const m = (brand as any)?.metrics && typeof (brand as any).metrics === 'object' ? (brand as any).metrics : null;
    const marginVal = m && typeof (m as any).margin === 'number' ? (m as any).margin : null;
    setMarginPercent(
      brand.calculation_id === 2 && typeof marginVal === 'number' && Number.isFinite(marginVal)
        ? String(Math.round(marginVal * 10000) / 100)
        : ''
    );

    // Prefill margin-by-qty settings (best-effort; shape may evolve)
    const metricsObj =
      (brand as any)?.metrics && typeof (brand as any).metrics === 'object' ? ((brand as any).metrics as any) : null;
    if (brand.calculation_id === 3 && metricsObj) {
      const prodIds = Array.isArray(metricsObj.product_ids)
        ? metricsObj.product_ids
            .map((v: any) => Number(v))
            .filter((n: any) => Number.isFinite(n))
        : [];
      setSelectedProductIds(Array.from(new Set(prodIds)));

      const gn = typeof metricsObj.group_name === 'string' ? metricsObj.group_name.trim() : '';
      setGroupName(gn);

      const rowsRaw = Array.isArray(metricsObj.matrix) ? metricsObj.matrix : [];
      const nextRows: MarginMatrixRow[] = rowsRaw
        .map((r: any) => {
          const margin = r?.margin;
          const qty = r?.quantity;
          const sp = r?.selling_price;

          let marginPct = '';
          if (typeof margin === 'number' && Number.isFinite(margin)) {
            // Accept either decimal (0.45) or percent (45)
            const pct = margin <= 1 ? margin * 100 : margin;
            marginPct = String(Math.round(pct * 100) / 100);
          }

          return {
            key: makeRowKey(),
            marginPercent: marginPct,
            quantity: qty === null || qty === undefined ? '' : String(qty),
            sellingPrice: sp === null || sp === undefined ? '' : String(sp),
          };
        })
        .filter(Boolean);

      setMatrixRows(
        nextRows.length > 0 ? nextRows : [{ key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' }]
      );
    } else {
      setSelectedProductIds([]);
      setGroupName('');
      setMatrixRows([{ key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' }]);
    }

    setProductFilter('');
    setProductOptions([]);
    setProductsError(null);

    setCalcSaveError(null);
    setCalcDialogOpen(true);
  }

  useEffect(() => {
    if (!calcDialogOpen) return;
    if (!calcBrand) return;
    if (calcOption !== 'margin_by_qty') return;
    void loadProductsForBrand(calcBrand.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcDialogOpen, calcBrand?.id, calcOption]);

  async function saveBrandCalculation() {
    if (!calcBrand || calcSaving) return;

    // Only wire /brands/{id}/ for now.
    let calculation_id: number;
    let metrics: any;

    if (calcOption === 'exact_cost') {
      calculation_id = 1;
      metrics = {};
    } else if (calcOption === 'margin_percentage') {
      calculation_id = 2;

      const p = Number(String(marginPercent ?? '').trim());
      if (!Number.isFinite(p) || p < 0 || p > 100) {
        setCalcSaveError('Please enter a valid percentage between 0 and 100.');
        return;
      }
      const marginDecimal = Number((p / 100).toFixed(6)); // 45 -> 0.45
      metrics = { margin: marginDecimal };
    } else {
      // calcOption === 'margin_by_qty' -> create a prod_special_calculation_group row
      const gn = String(groupName ?? '').trim();
      if (!gn) {
        setCalcSaveError('Please enter a group name.');
        return;
      }
      if (selectedProductIds.length === 0) {
        setCalcSaveError('Please select at least one product.');
        return;
      }

      // Build metrics: [{ quantity, selling_price, margin }, ...]
      const nextMetrics = (matrixRows || [])
        .map((r) => {
          const qty = Number(String(r.quantity ?? '').trim());
          const sellingPrice = Number(String(r.sellingPrice ?? '').trim());
          const marginPct = Number(String(r.marginPercent ?? '').trim());

          if (!Number.isFinite(qty) || qty <= 0) return null;
          if (!Number.isFinite(sellingPrice) || sellingPrice < 0) return null;
          if (!Number.isFinite(marginPct) || marginPct < 0 || marginPct > 100) return null;

          const margin = Number((marginPct / 100).toFixed(6));
          return { quantity: Math.trunc(qty), selling_price: sellingPrice, margin };
        })
        .filter(Boolean) as Array<{ quantity: number; selling_price: number; margin: number }>;

      if (nextMetrics.length === 0) {
        setCalcSaveError('Please add at least one valid margin matrix row.');
        return;
      }

      setCalcSaving(true);
      setCalcSaveError(null);
      try {
        const resp = await fetch('/v1/api/gp-reporter/special-calculation-groups/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group_name: gn,
            product_id: selectedProductIds,
            metrics: nextMetrics,
            favourite: true,
          }),
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
        }

        // Created successfully. Close dialog.
        setCalcDialogOpen(false);
        setCalcBrand(null);
      } catch (e) {
        setCalcSaveError(e instanceof Error ? e.message : 'Failed to create special calculation group.');
      } finally {
        setCalcSaving(false);
      }
      return;
    }

    setCalcSaving(true);
    setCalcSaveError(null);

    // Optimistic UI update for this brand row.
    const prev = brands.find((b) => b.id === calcBrand.id);
    setBrands((current) =>
      current.map((b) =>
        b.id === calcBrand.id
          ? {
              ...b,
              calculation_id,
              metrics,
            }
          : b
      )
    );

    try {
      const resp = await fetch(`/v1/api/gp-reporter/brands/${calcBrand.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ calculation_id, metrics }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      // Reload to refresh calculation_description from backend join.
      await loadBrands();
      setCalcDialogOpen(false);
      setCalcBrand(null);
    } catch (e) {
      if (prev) setBrands((current) => current.map((b) => (b.id === prev.id ? prev : b)));
      setCalcSaveError(e instanceof Error ? e.message : 'Failed to update brand calculation.');
    } finally {
      setCalcSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Refresh */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search brands..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={loadBrands} disabled={loading} className="gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Refresh
        </Button>
      </div>

      {/* Showing count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredBrands.length} of {brands.length} brands
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[320px]">Brand Name</TableHead>
              <TableHead>Calculation</TableHead>
              <TableHead className="w-[120px] text-center">Favourite</TableHead>
              <TableHead className="w-[90px] text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredBrands.length > 0 ? (
              filteredBrands.map((brand) => (
                <TableRow key={brand.id}>
                  <TableCell>{brand.brand_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">
                        {brand.calculation_description ?? '—'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Select
                      value={brand.favourite ? 'true' : 'false'}
                      onValueChange={(v) => setBrandFavourite({ brandId: brand.id, favourite: v === 'true' })}
                      disabled={loading || updatingBrandIds.has(brand.id)}
                    >
                      <SelectTrigger className="h-8 w-[110px] text-xs font-normal mx-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openCalcDialog(brand)}>
                          Set brand calculation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                  {loading ? 'Loading brands…' : 'No brands found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={calcDialogOpen} onOpenChange={setCalcDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Set brand calculation</DialogTitle>
            <DialogDescription>
              {calcBrand ? (
                <>
                  Brand: <span className="font-medium">{calcBrand.brand_name}</span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col md:flex-row md:items-stretch">
            {/* Left: choices */}
            <div className="md:w-[340px] w-full">
              <div className="space-y-3" role="radiogroup" aria-label="Calculation options">
                <Card
                  className={`cursor-pointer transition-colors ${
                    calcOption === 'exact_cost' ? 'border-primary' : 'hover:bg-muted/30'
                  }`}
                  onClick={() => setCalcOption('exact_cost')}
                  role="radio"
                  aria-checked={calcOption === 'exact_cost'}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setCalcOption('exact_cost');
                  }}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ${
                          calcOption === 'exact_cost' ? 'border-primary' : 'border-muted-foreground/40'
                        }`}
                        aria-hidden="true"
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${
                            calcOption === 'exact_cost' ? 'bg-primary' : 'bg-transparent'
                          }`}
                        />
                      </span>
                      <div className="space-y-1">
                        <CardTitle className="text-sm font-medium">Exact Cost</CardTitle>
                        <CardDescription className="text-xs">Direct net profit calculation</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card
                  className={`cursor-pointer transition-colors ${
                    calcOption === 'margin_percentage' ? 'border-primary' : 'hover:bg-muted/30'
                  }`}
                  onClick={() => setCalcOption('margin_percentage')}
                  role="radio"
                  aria-checked={calcOption === 'margin_percentage'}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setCalcOption('margin_percentage');
                  }}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ${
                          calcOption === 'margin_percentage' ? 'border-primary' : 'border-muted-foreground/40'
                        }`}
                        aria-hidden="true"
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${
                            calcOption === 'margin_percentage' ? 'bg-primary' : 'bg-transparent'
                          }`}
                        />
                      </span>
                      <div className="space-y-1">
                        <CardTitle className="text-sm font-medium">Margin Percentage</CardTitle>
                        <CardDescription className="text-xs">Apply a fixed margin amount</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <Card
                  className={`cursor-pointer transition-colors ${
                    calcOption === 'margin_by_qty' ? 'border-primary' : 'hover:bg-muted/30'
                  }`}
                  onClick={() => setCalcOption('margin_by_qty')}
                  role="radio"
                  aria-checked={calcOption === 'margin_by_qty'}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setCalcOption('margin_by_qty');
                  }}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border ${
                          calcOption === 'margin_by_qty' ? 'border-primary' : 'border-muted-foreground/40'
                        }`}
                        aria-hidden="true"
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${
                            calcOption === 'margin_by_qty' ? 'bg-primary' : 'bg-transparent'
                          }`}
                        />
                      </span>
                      <div className="space-y-1">
                        <CardTitle className="text-sm font-medium">Margin Percentage by Quantity</CardTitle>
                        <CardDescription className="text-xs">
                          Apply a fixed margin amount based on quantity groupings
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </div>
            </div>

            {/* Separator: vertical on md+, horizontal on mobile */}
            <div className="md:px-4 md:py-0 py-4">
              <Separator orientation="horizontal" className="md:hidden" />
              <Separator orientation="vertical" className="hidden md:block h-full" />
            </div>

            {/* Right: fields */}
            <div className="flex-1 min-w-0 flex flex-col">
              {/* Scrollable content area (prevents Save button from being pushed out) */}
              <div className="max-h-[60vh] overflow-auto pr-2">
                {calcOption === 'exact_cost' && (
                  <div className="text-sm text-muted-foreground">No additional settings.</div>
                )}

                {calcOption === 'margin_percentage' && (
                  <div className="space-y-2 max-w-sm">
                    <Label htmlFor="margin-percentage">Margin percentage (%)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="margin-percentage"
                        type="number"
                        inputMode="decimal"
                        placeholder="e.g. 45"
                        value={marginPercent}
                        onChange={(e) => setMarginPercent(e.currentTarget.value)}
                        className="h-9"
                      />
                      <Badge variant="outline" className="font-normal">
                        %
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This will apply a fixed margin percentage for the brand.
                    </p>
                  </div>
                )}

                {calcOption === 'margin_by_qty' && (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="group-name">Group name</Label>
                      <Input
                        id="group-name"
                        placeholder="e.g. Teaset"
                        value={groupName}
                        onChange={(e) => setGroupName(e.currentTarget.value)}
                        className="h-9"
                      />
                      <p className="text-xs text-muted-foreground">
                        This will be inserted as a new row in special calculation groups.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Products (multi-select)</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-9 justify-between font-normal text-foreground"
                            disabled={productsLoading || !calcBrand}
                          >
                            <span className="truncate">
                              {selectedProductIds.length === 0
                                ? 'Select products'
                                : `${selectedProductIds.length} selected`}
                            </span>
                            <ChevronDown className="h-4 w-4 opacity-60" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[420px] max-w-[80vw]" align="start">
                          <DropdownMenuLabel>Select products</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <div className="p-2">
                            <Input
                              placeholder="Search by title or SKU..."
                              value={productFilter}
                              onChange={(e) => setProductFilter(e.currentTarget.value)}
                              className="h-8 text-sm"
                            />
                          </div>
                          <DropdownMenuSeparator />
                          <div className="max-h-64 overflow-auto">
                            {productOptions
                              .filter((p) => {
                                const q = productFilter.trim().toLowerCase();
                                if (!q) return true;
                                const t = `${p.main_title ?? ''} ${p.sku ?? ''}`.toLowerCase();
                                return t.includes(q);
                              })
                              .slice(0, 250)
                              .map((p) => {
                                const checked = selectedProductIds.includes(p.id);
                                return (
                                  <DropdownMenuCheckboxItem
                                    key={p.id}
                                    checked={checked}
                                    onCheckedChange={(c) => {
                                      const nextChecked = Boolean(c);
                                      setSelectedProductIds((cur) => {
                                        if (nextChecked) return Array.from(new Set([...cur, p.id]));
                                        return cur.filter((x) => x !== p.id);
                                      });
                                    }}
                                  >
                                    <div className="min-w-0">
                                      <div className="text-sm truncate">{p.main_title || '(no title)'}</div>
                                      {p.sku ? (
                                        <div className="text-xs text-muted-foreground font-mono truncate">{p.sku}</div>
                                      ) : null}
                                    </div>
                                  </DropdownMenuCheckboxItem>
                                );
                              })}
                            {!productsLoading && productOptions.length === 0 && (
                              <div className="px-3 py-2 text-sm text-muted-foreground">No products found.</div>
                            )}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      {productsError && (
                        <div className="flex items-start gap-2 text-sm text-destructive">
                          <AlertTriangle className="h-4 w-4 mt-0.5" />
                          <span className="break-words">{productsError}</span>
                        </div>
                      )}

                      {selectedProductIds.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {selectedProductIds
                            .map((id) => productOptions.find((p) => p.id === id))
                            .filter(Boolean)
                            .map((p) => (
                              <Badge key={p!.id} variant="outline" className="font-normal">
                                <span className="max-w-[220px] truncate">{p!.main_title || p!.sku || `#${p!.id}`}</span>
                                <button
                                  type="button"
                                  className="ml-1 inline-flex items-center opacity-70 hover:opacity-100"
                                  onClick={() =>
                                    setSelectedProductIds((cur) => cur.filter((x) => x !== p!.id))
                                  }
                                  aria-label="Remove"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Margin matrix</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs font-normal gap-1"
                          onClick={() =>
                            setMatrixRows((cur) => [
                              ...cur,
                              { key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' },
                            ])
                          }
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add row
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {matrixRows.map((row, idx) => (
                          <div key={row.key} className="rounded-md border p-3">
                            <div className="grid grid-cols-12 gap-3">
                              <div className="col-span-12 md:col-span-4">
                                <Label className="text-xs" htmlFor={`marg-${row.key}`}>
                                  Margin (%) {matrixRows.length > 1 ? `#${idx + 1}` : ''}
                                </Label>
                                <div className="mt-1 flex items-center gap-2">
                                  <Input
                                    id={`marg-${row.key}`}
                                    type="number"
                                    inputMode="decimal"
                                    placeholder="e.g. 45"
                                    value={row.marginPercent}
                                    onChange={(e) => {
                                      const v = e.currentTarget.value;
                                      setMatrixRows((cur) =>
                                        cur.map((r) => (r.key === row.key ? { ...r, marginPercent: v } : r))
                                      );
                                    }}
                                    className="h-9"
                                  />
                                  <Badge variant="outline" className="font-normal">
                                    %
                                  </Badge>
                                </div>
                              </div>

                              <div className="col-span-12 md:col-span-4">
                                <Label className="text-xs" htmlFor={`qty-${row.key}`}>
                                  Quantity
                                </Label>
                                <Input
                                  id={`qty-${row.key}`}
                                  type="number"
                                  inputMode="numeric"
                                  placeholder="e.g. 2"
                                  value={row.quantity}
                                  onChange={(e) => {
                                    const v = e.currentTarget.value;
                                    setMatrixRows((cur) =>
                                      cur.map((r) => (r.key === row.key ? { ...r, quantity: v } : r))
                                    );
                                  }}
                                  className="h-9 mt-1"
                                />
                              </div>

                              <div className="col-span-12 md:col-span-4">
                                <Label className="text-xs" htmlFor={`sp-${row.key}`}>
                                  Selling price
                                </Label>
                                <Input
                                  id={`sp-${row.key}`}
                                  type="number"
                                  inputMode="decimal"
                                  placeholder="e.g. 199.90"
                                  value={row.sellingPrice}
                                  onChange={(e) => {
                                    const v = e.currentTarget.value;
                                    setMatrixRows((cur) =>
                                      cur.map((r) => (r.key === row.key ? { ...r, sellingPrice: v } : r))
                                    );
                                  }}
                                  className="h-9 mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <p className="text-xs text-muted-foreground">
                        Add rows to define margin rules by quantity and selling price.
                      </p>
                    </div>
                  </div>
                )}

                {calcSaveError && (
                  <div className="mt-4 flex items-start gap-2 text-sm text-destructive">
                    <AlertTriangle className="h-4 w-4 mt-0.5" />
                    <span className="break-words">{calcSaveError}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setCalcDialogOpen(false)} disabled={calcSaving}>
                  Cancel
                </Button>
                <Button onClick={saveBrandCalculation} disabled={calcSaving}>
                  {calcSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
